// The effect behind each menu item. Server calls go through calendar-api.ts (T2 bridge) or
// http-client.ts's postAndNavigate; dialog-opening actions call the still-jQuery openEventDialog
// global (dialog rewrite is a separate future step, per docs/plans/t2-bridge-and-context-menu.md §5.2).

import { executeUndo, saveEventColor, undoPreview } from '../calendar-api';
import { HttpError, postAndNavigate } from '../http-client';
import { dateFromColumn, openEventDialog, redrawCalendar, t } from './legacy';

export function addGroupAction(rowId: number, column: number, groupId: string, groupName: string): () => void {
  return () => {
    const dateFrom = dateFromColumn(column);
    const url = `event_create.php?calendarRowId=${rowId}&calendarRowGroup=${groupId}&groupName=${encodeURIComponent(groupName)}&dateFrom=${dateFrom}&dateTo=${dateFrom}`;
    openEventDialog(url);
  };
}

export function pasteAction(rowId: number, column: number): () => void {
  return () => {
    const eventId = sessionStorage.getItem('eventId');
    if (!eventId) {
      alert(t('js.copy_no_selection', 'First select an event to copy.'));
      return;
    }
    const dateFrom = dateFromColumn(column);
    const duration = sessionStorage.getItem('duration');
    const cut = sessionStorage.getItem('cut');
    const url = `event_copy.php?eventId=${eventId}&calendarRowId=${rowId}&dateFrom=${dateFrom}&duration=${duration}&cut=${cut}`;
    openEventDialog(url);
  };
}

function isSessionExpired(err: unknown): boolean {
  return err instanceof Error && err.name === 'SessionExpiredError';
}

export function undoAction(): () => void {
  return () => {
    undoPreview()
      .then((preview) => {
        if (!preview.ok) {
          alert(preview.error || t('js.undo_load_error', 'Cannot load last operation.'));
          return;
        }
        if (!confirm(preview.prompt || t('js.ctx_undo_confirm', 'Really undo the last change?'))) return;
        executeUndo()
          .then((result) => {
            if (result.ok) {
              redrawCalendar();
            } else {
              alert(result.error || t('js.undo_execute_error', 'Cannot perform undo.'));
            }
          })
          .catch((err) => {
            if (isSessionExpired(err)) {
              alert(t('js.session_expired', 'Your session has expired. Please log in again.'));
              return;
            }
            alert((err instanceof HttpError && err.serverMessage) || t('js.undo_call_error', 'Error calling undo step.'));
          });
      })
      .catch((err) => {
        if (isSessionExpired(err)) {
          alert(t('js.session_expired', 'Your session has expired. Please log in again.'));
          return;
        }
        alert(t('js.undo_prepare_error', 'Error preparing undo step.'));
      });
  };
}

export function copyAction(eventEl: HTMLElement): () => void {
  return () => {
    sessionStorage.setItem('eventId', eventEl.getAttribute('data-href') ?? '');
    sessionStorage.setItem('duration', eventEl.getAttribute('data-dur') ?? '');
    sessionStorage.setItem('cut', 'false');
  };
}

export function cutAction(eventEl: HTMLElement): () => void {
  return () => {
    sessionStorage.setItem('eventId', eventEl.getAttribute('data-href') ?? '');
    sessionStorage.setItem('duration', eventEl.getAttribute('data-dur') ?? '');
    sessionStorage.setItem('cut', 'true');
  };
}

export function deleteAction(eventEl: HTMLElement): () => void {
  return () => {
    if (!confirm(t('js.event_delete_confirm', 'Do you want to delete this event?'))) return;
    postAndNavigate('event_delete.php', { eventId: eventEl.getAttribute('data-href') ?? '', csrf_token: window.csrfToken ?? '' });
  };
}

export function feedbackAction(eventEl: HTMLElement): () => void {
  return () => openEventDialog(`feedback.php?eventId=${eventEl.getAttribute('data-href') ?? ''}`);
}

export function notificationAction(eventEl: HTMLElement): () => void {
  return () => openEventDialog(`event_notification_dialog.php?eventId=${eventEl.getAttribute('data-href') ?? ''}`);
}

// -- Colour palette (custom-mode users; ported from calendar.v2.8.js:812-992, the only caller) --

export const USER_COLOR_PALETTE = [
  { id: 'urgent', i18nKey: 'js.color_red', fallback: 'Red', hex: '#f44336' },
  { id: 'waiting', i18nKey: 'js.color_yellow', fallback: 'Yellow', hex: '#ffeb3b' },
  { id: 'done', i18nKey: 'js.color_green', fallback: 'Green', hex: '#4caf50' },
];

function computeContrastText(bg: string): string {
  let hex = bg.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? '#000' : '#fff';
}

function applyColorHexInline(eventEl: HTMLElement, colorHex: string, textColor: string): void {
  if (!colorHex) {
    const originalBg = eventEl.getAttribute('data-original-bg');
    const originalTextColor = eventEl.getAttribute('data-original-text-color');
    eventEl.style.background = originalBg || '';
    eventEl.style.color = originalTextColor || '';
    eventEl.removeAttribute('data-user-color');
    const note = eventEl.getAttribute('data-user-note');
    if (!note) eventEl.classList.remove('custom-colored');
    return;
  }
  eventEl.setAttribute('data-user-color', colorHex);
  eventEl.classList.add('custom-colored');
  const originalBg = eventEl.getAttribute('data-original-bg') || '';
  eventEl.style.background = `linear-gradient(to bottom right, ${originalBg} 50%, ${colorHex} 50%)`;
  if (textColor) eventEl.style.color = textColor;
}

let toastEl: HTMLDivElement | null = null;
function toastTemporary(message: string, isError: boolean): void {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.id = 'calendar-toast';
    toastEl.style.cssText =
      'position:fixed;bottom:12px;left:50%;transform:translateX(-50%);background:#333;color:#fff;padding:6px 12px;border-radius:4px;font-size:12px;z-index:9999;opacity:0;transition:opacity .25s;';
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = message;
  toastEl.style.background = isError ? '#b30000' : '#333';
  toastEl.style.opacity = '1';
  window.setTimeout(() => {
    if (toastEl) toastEl.style.opacity = '0';
  }, 2000);
}

export function colorAction(eventEl: HTMLElement, colorHex: string): () => void {
  return () => {
    const eventId = eventEl.getAttribute('data-href');
    if (!eventId) return;
    const textColor = colorHex ? computeContrastText(colorHex) : '';
    saveEventColor(eventId, colorHex, textColor)
      .then((result) => {
        if (result.success) {
          applyColorHexInline(eventEl, colorHex, textColor);
          toastTemporary(t('js.color_saved', 'Color saved.'), false);
        } else {
          toastTemporary(t('js.color_save_error', 'Error saving color.'), true);
        }
      })
      .catch((err) => {
        if (isSessionExpired(err)) {
          toastTemporary(t('js.session_expired', 'Your session has expired. Please log in again.'), true);
          return;
        }
        toastTemporary(t('js.network_error', 'Network error.'), true);
      });
  };
}
